/**
 * Product Sibling Selector with Section Rendering
 * Uses section rendering to update product information when sibling changes
 */

class ProductSiblingSelector {
  constructor() {
    this.selectors = document.querySelectorAll('.sibling-selector');
    this.initCache();
    this.init();
    this.setupPreloading();
  }

  initCache() {
    if (!window.productSiblingCache) {
      window.productSiblingCache = new Map();
    }
    this.cache = window.productSiblingCache;
    this.preloadStarted = false;
  }

  init() {
    this.selectors.forEach(selector => {
      selector.addEventListener('sibling-change', this.handleSiblingChange.bind(this));
    });
  }

  async handleSiblingChange(event) {
    const { url } = event.detail;
    
    try {
      // Show loading state
      this.showLoadingState(event.target);
      
      // Check cache first
      let html = this.cache.get(url);
      
      if (!html) {
        // Fetch the full sibling product page if not cached
        const response = await fetch(url);
        html = await response.text();
        
        // Store in cache
        this.cache.set(url, html);
      }
      
      // Parse the HTML and extract the main-product section
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      const newMainProduct = tempDiv.querySelector('[data-section-type="main-product"]');
      
      if (newMainProduct) {
        // Replace the current main-product section with the new one
        const currentMainProduct = document.querySelector('[data-section-type="main-product"]');
        if (currentMainProduct) {
          // Animate out the current section
          currentMainProduct.style.transition = 'opacity 0.3s ease-in-out';
          currentMainProduct.style.opacity = '0';
          
          setTimeout(() => {
            // Replace the section
            currentMainProduct.parentNode.replaceChild(newMainProduct, currentMainProduct);
            
            // Animate in the new section
            newMainProduct.style.transition = 'opacity 0.3s ease-in-out';
            newMainProduct.style.opacity = '0';
            
            // Force reflow
            newMainProduct.offsetHeight;
            
            // Fade in
            newMainProduct.style.opacity = '1';
            
            // Update URL without page reload
            window.history.pushState(null, '', url);
            
            // Reinitialize components
            this.reinitializeComponents();
            
            // Reload scripts after transition
            this.reloadScripts();
            
            // Hide loading state
            this.hideLoadingState(event.target);
          }, 300);
        }
      }

      window.initModernAppstleWidget();
      
    } catch (error) {
      console.error('Error updating sibling product:', error);
      this.hideLoadingState(event.target);
      
      // Fallback to navigation
      window.location.href = url;
    }
  }


  reinitializeComponents() {
    // Reinitialize Appstle widget
    if (typeof initModernAppstleWidget === 'function') {
      initModernAppstleWidget();
    }

    // Reinitialize other product components as needed
    this.reinitializeSiblingSelector();
  }

  reinitializeSiblingSelector() {
    // Reinitialize sibling selector for the new product
    const newSelectors = document.querySelectorAll('.sibling-selector:not([data-initialized])');
    newSelectors.forEach(selector => {
      selector.addEventListener('sibling-change', this.handleSiblingChange.bind(this));
      selector.setAttribute('data-initialized', 'true');
    });
  }

  reloadScripts() {
    // Trigger DOMContentLoaded for new content
    document.dispatchEvent(new Event('DOMContentLoaded'));
    
    // Reinitialize common product components
    this.reinitializeProductComponents();
    
    // Find and execute script tags in the new main-product section
    const mainProduct = document.querySelector('[data-section-type="main-product"]');
    if (mainProduct) {
      const scripts = mainProduct.querySelectorAll('script');
      scripts.forEach(script => {
        if (script.textContent && !script.src) {
          // Inline script - execute it
          try {
            const func = new Function(script.textContent);
            func();
          } catch (error) {
            console.error('Error executing inline script:', error, script.textContent);
          }
        }
      });
    }
  }

  reinitializeProductComponents() {
    // Reinitialize product slider/gallery
    if (typeof ProductSlider !== 'undefined') {
      const productSliders = document.querySelectorAll('product-slider');
      productSliders.forEach(slider => {
        if (slider && typeof slider.connectedCallback === 'function') {
          slider.connectedCallback();
        }
      });
    }

    // Reinitialize variant selectors
    if (typeof VariantSelects !== 'undefined') {
      const variantSelects = document.querySelectorAll('variant-selects');
      variantSelects.forEach(select => {
        if (select && typeof select.connectedCallback === 'function') {
          select.connectedCallback();
        }
      });
    }

    // Reinitialize product forms
    if (typeof ProductForm !== 'undefined') {
      const productForms = document.querySelectorAll('product-form');
      productForms.forEach(form => {
        if (form && typeof form.connectedCallback === 'function') {
          form.connectedCallback();
        }
      });
    }
  }

  showLoadingState(selector) {
    const container = selector.closest('.sibling-selector__container');
    if (container) {
      container.classList.add('loading');
      container.style.opacity = '0.5';
      container.style.pointerEvents = 'none';
    }
  }

  hideLoadingState(selector) {
    const container = selector.closest('.sibling-selector__container');
    if (container) {
      container.classList.remove('loading');
      container.style.opacity = '1';
      container.style.pointerEvents = 'auto';
    }
  }

  setupPreloading() {
    const startPreloading = () => {
      if (this.preloadStarted) return;
      this.preloadStarted = true;
      
      // Use requestIdleCallback for non-blocking preloading
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => this.preloadSiblingPages());
      } else {
        setTimeout(() => this.preloadSiblingPages(), 100);
      }
    };

    // Start preloading after first user interaction
    const events = ['click', 'scroll', 'touchstart', 'touchmove', 'mouseenter', 'mousemove', 'pointerenter', 'pointermove'];
    events.forEach(event => {
      document.addEventListener(event, startPreloading, { once: true, passive: true });
    });
  }

  async preloadSiblingPages() {
    const siblingInputs = document.querySelectorAll('.sibling-selector__radio[data-sibling-url]');
    
    for (const input of siblingInputs) {
      const url = input.dataset.siblingUrl;
      
      // Skip if already cached or is current page
      if (this.cache.has(url) || url === window.location.pathname) {
        continue;
      }
      
      try {
        // Add small delay between requests to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const response = await fetch(url);
        const html = await response.text();
        
        this.cache.set(url, html);
        console.log(`Preloaded: ${url}`);
        
      } catch (error) {
        console.warn(`Failed to preload ${url}:`, error);
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new ProductSiblingSelector();
});